<template>
    <div id="play-catalog-page" class="card mb-5">
        <div class="card-header">
            <div class="row">
                <div class="col-12">
                    <div class="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-3 gap-sm-0">
                        <div class="d-flex gap-2">
                            <nav class="navbar navbar-expand tertiary-tab-bar">
                                <ul class="navbar-nav">
                                    <li class="nav-item">
                                        <a
                                            class="nav-link d-flex flex-nowrap tertiary-tab"
                                            :class="{ 'tertiary-tab-active': viewMode === 'grid' }"
                                            href="#"
                                            @click.prevent="setViewMode('grid')"
                                            data-bs-toggle="tooltip"
                                            title="Grid view"
                                        >
                                            <span><img src="/img/icons/avid-icons/grid-view.svg" class="icon" alt=""></span>
                                        </a>
                                    </li>
                                    <li class="nav-item">
                                        <a
                                            class="nav-link d-flex flex-nowrap tertiary-tab"
                                            :class="{ 'tertiary-tab-active': viewMode === 'list' }"
                                            href="#"
                                            @click.prevent="setViewMode('list')"
                                            data-bs-toggle="tooltip"
                                            title="List view"
                                        >
                                            <span><img src="/img/icons/avid-icons/list-view.svg" class="icon" alt=""></span>
                                        </a>
                                    </li>
                                </ul>
                            </nav>                                     
                        </div>

                        <div class="d-flex flex-column flex-sm-row gap-2">
                            <div class="position-relative">
                                <input type="text" placeholder="Search plays..." aria-label="Search plays..." v-model="search_key" @input="handleFilterSelection()" autocomplete="one-time-code" class="search-box form-control me-sm-1 py-2" style="min-width: 250px;">
                                <i class="fa fa-search search-placeholder-icon" v-if="!search_key"></i>
                            </div>

                            <button class="btn btn-secondary me-sm-1 py-0 dropdown-toggle dropdown-toggle-no-btn w-100 w-sm-auto" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                                <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">Filters
                            </button>
                            <div class="dropdown-menu dropdown-menu-primary p-3" style="width: 320px;">
                                <p>Channel:</p>
                                <accordion-select 
                                    v-model="filters.asset_type" 
                                    :options="all_asset_types" 
                                    @update:modelValue="handleFilterSelection"
                                ></accordion-select>

                                <p class="mt-4">Strategic Focus:</p>
                                <accordion-select 
                                    v-model="filters.strategy" 
                                    :options="all_strategies" 
                                    @update:modelValue="handleFilterSelection"
                                ></accordion-select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card-body">
            <!-- No plays found message -->
            <div v-if="filtered_playbook_types.length === 0" class="text-center py-5">
                <h5 class="text-muted mb-2">No plays found</h5>
                <p class="text-muted mb-0">Try adjusting your search or filter criteria to find what you're looking for.</p>
            </div>

            <!-- Grid View -->
            <div v-else-if="viewMode === 'grid'" class="playbook-grid">
                <!-- Need help? Card -->
                <div class="playbook-card">
                    <div class="card play-card">
                        <div class="card-header">
                            <div class="play-image-container">
                                <img src="/img/images/playbook_card.png" class="play-image" alt="Play image">
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-12 mb-3">
                                    <h4>Smart Play</h4>
                                </div>
                                <div class="col-12 mb-0">
                                    <p class="small">Need help? Describe your campaign and we'll customize a play to meet your needs.</p>
                                </div>
                            </div>
                        </div>
                        <div class="card-footer">
                            <div class="d-flex justify-content-between align-items-center">
                                <button @click="$emit('open-dive-deeper')" class="btn btn-secondary btn-icon-only-sm btn-sm" data-bs-toggle="tooltip" title="Detailed view with extended insights and context">
                                    <img src="/img/icons/avid-icons/deep-dive.svg" class="icon" alt="">
                                </button>
                                <div class="play-actions d-flex gap-2">
                                    <button v-if="canEdit('playbooks')" class="btn btn-primary btn-primary-playbook btn-sm px-3" @click="$emit('open-need-help')"><img src="/img/icons/avid-icons/edna.svg" class="icon me-1" alt=""> Start</button>
                                    <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-primary btn-primary-playbook btn-sm px-3" disabled><img src="/img/icons/avid-icons/edna.svg" class="icon me-1" alt=""> Start</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-for="playbook_type in filtered_playbook_types" :key="playbook_type.id" class="playbook-card">
                    <div class="card play-card" >
                        <div class="card-header">
                            <div class="play-image-container">
                                <img :src="playbook_type.thumbnail_image || '/img/images/suggested-play-img.png'" class="play-image" alt="Play image">
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-12 mb-3">
                                    <h4>{{ playbook_type.name }}</h4>
                                </div>

                                <div class="col-6 mb-2">
                                    <span class="complexity-label">Focus</span>
                                </div>
                                <div class="col-6 mb-2">
                                    <div class="d-flex flex-wrap gap-1">
                                        <span v-for="(strategy, index) in playbook_type.strategy" :key="index" class="badge" v-if="playbook_type.strategy">
                                            {{ strategy }}
                                        </span>
                                        <span v-else class="badge">Conversion</span>
                                    </div>
                                </div>
                                <div class="col-6 mb-2">
                                    <span class="complexity-label">Complexity</span>
                                </div>
                                <div class="col-6 mb-2">
                                    <div class="complexity-dots">
                                        <div v-for="level in 5" :key="level" 
                                            class="complexity-dot" 
                                            :class="{ 'active': level <= playbook_type.complexity_level }">
                                        </div>
                                    </div>
                                </div>

                                <div class="col-6 mb-2">
                                    <span class="complexity-label">Runtime</span>
                                </div>
                                <div class="col-6 mb-2">
                                    <span class="complexity-label" v-html="getEstimatedTimeToCreate(playbook_type)"></span>
                                </div>
                            </div>
                        </div>
                            
                        <div class="card-footer">
                            <div class="d-flex justify-content-between align-items-center">
                                <button @click="openPlaybookDescription(playbook_type)" class="btn btn-secondary btn-icon-only-sm btn-sm" 
                                data-bs-toggle="tooltip" title="Detailed view with extended insights and context">
                                    <img src="/img/icons/avid-icons/deep-dive.svg" class="icon" alt="">
                                </button>

                                <div class="play-actions d-flex gap-2">
                                    <a v-if="canEdit('playbooks') && playbook_type.id === 0 && client && client.url" class="btn btn-primary btn-sm px-4 font-weight-normal" 
                                        :href="'/experiment_treatments/'+ client.url + '/new'">
                                        Start
                                    </a>
                                    <button v-else-if="canEdit('playbooks')" @click="openPlaybookCreation(playbook_type)" class="btn btn-primary btn-sm px-4 font-weight-normal">
                                        Start
                                    </button>
                                    <div v-else class="float-end" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-primary btn-sm px-4 font-weight-normal" :disabled="true">
                                            Start
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- List View -->
            <div v-else-if="viewMode === 'list'" class="p-0 table-card play-table-view">
                <table class="table table-width-responsive table-with-pagination mb-0">
                    <thead>
                        <tr class="table-header-row">
                            <th @click="sortByKey('name')" class="d-table-cell bg-white">
                                <div class="d-flex flex-row">
                                    Name
                                    <div class="d-flex flex-column">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sort_key === 'name' && sort_order === 'asc'}" alt="">
                                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sort_key === 'name' && sort_order === 'des'}" alt="">
                                    </div>
                                </div>
                            </th>
                            <th class="d-table-cell bg-white">
                                <div class="d-flex flex-row">
                                    Focus
                                </div>
                            </th>
                            <th class="d-none d-lg-table-cell bg-white column-sort-disabled" style="width: 200px;">
                                <div class="d-flex flex-row">
                                    Channels
                                </div>
                            </th>
                            <th class="d-none d-md-table-cell bg-white">
                                <div class="d-flex flex-row">
                                    Runtime
                                </div>
                            </th>
                            <th class="table-column-action column-sort-disabled d-table-cell bg-white pe-3" width="1"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="need-help-row">
                            <td class="d-table-cell align-start py-3">
                                <a href="#" class="text-decoration-none fw-semibold"  @click="$emit('open-need-help')">Build a Smart Play</a>
                            </td>
                            <td class="d-table-cell">—</td>
                            <td class="d-none d-lg-table-cell">—</td>
                            <td class="d-none d-md-table-cell">—</td>
                            <td class="column-sort-disabled d-table-cell">
                                <div class="d-flex justify-content-center">
                                    <button v-if="canEdit('playbooks')" class="btn btn-primary btn-sm px-4" style="min-width: 50px;" @click="$emit('open-need-help')">
                                        <img src="/img/icons/avid-icons/edna.svg" class="icon me-1" alt=""> Start
                                    </button>
                                    <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-primary btn-sm px-4" :disabled="true" style="min-width: 50px;">
                                            <img src="/img/icons/avid-icons/edna.svg" class="icon me-1" alt=""> Start
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="(playbook_type, index) in list_shown" :key="playbook_type.id" :class="`table-row-${index + 1}`">
                            <td class="d-table-cell align-start">
                                <a @click="openPlaybookDescription(playbook_type)" href="#" class="text-decoration-none">{{ playbook_type.name }}</a>
                            </td>
                            <td class="d-table-cell">
                                <div class="d-flex flex-wrap gap-1">
                                    <span v-for="(strategy, index) in playbook_type.strategy" :key="index" class="badge bg-secondary">
                                        {{ strategy }}
                                    </span>
                                    <span v-if="!playbook_type.strategy || playbook_type.strategy.length === 0" class="badge bg-secondary">
                                        Other
                                    </span>
                                </div>
                            </td>
                            <td class="d-none d-lg-table-cell channels-column">
                                <div class="position-relative text-nowrap">
                                    <template v-for="(asset_type, index) in getUniqueAssetTypes(playbook_type.asset_types)" :key="asset_type.id">
                                        <button class="btn btn-secondary btn-icon-only-sm" data-bs-toggle="tooltip" :title="asset_type.name" disabled>
                                            <img :src="'/img/icons/avid-icons/' + asset_type.icon_class + '.svg'" class="asset-type-icon icon" alt=""></img>
                                        </button>
                                    </template>
                                </div>
                            </td>
                            <td class="d-none d-md-table-cell">
                                <span v-html="getEstimatedTimeToCreate(playbook_type)"></span>
                            </td>
                            <td class="column-sort-disabled d-table-cell">
                                <div class="d-flex justify-content-center">
                                    <a v-if="canEdit('playbooks') && playbook_type.id === 0 && client && client.url" class="btn btn-primary btn-sm px-4" 
                                        :href="'/experiment_treatments/'+ client.url + '/new'">
                                        Start
                                    </a>
                                    <button v-else-if="canEdit('playbooks')" @click="openPlaybookCreation(playbook_type)" class="btn btn-primary btn-sm px-4" style="min-width: 50px;">
                                        Start
                                    </button>
                                    <div v-else data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                                        <button class="btn btn-primary btn-sm px-4" :disabled="true" style="min-width: 50px;">
                                            Start
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Pagination -->
                <div class="table-border-bottom-0">
                    <div class="table-pagination">
                        <div class="table-pagination--group d-none d-md-flex">
                            <div class="position-relative">
                                <select class="form-select" v-model="num_per_page" @change="reloadPagination(filtered_playbook_types)" aria-label="Num Per Page">
                                    <option value="5000">ALL</option>
                                    <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                                </select>
                                <div class="select-display-text">
                                    Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                                </div>
                            </div>
                        </div>

                        <div class="table-pagination--group d-none d-lg-flex">
                            {{ filtered_playbook_types.length ==0 ? '0' : item_interval }} of {{ filtered_playbook_types.length }}
                        </div>

                        <div class="table-pagination--group">
                            <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click="moveToPrevPage(filtered_playbook_types)" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                                <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                            </button>

                            <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                                <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="page === (page_index + 1) ? 'current-page' : ''" @click="moveToPage(page, filtered_playbook_types)">
                                    {{ page }}
                                </button>
                            </div>
                            <div class="d-flex page-selector" v-else>
                                <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="page === (page_index + 1) ? 'current-page' : ''" @click="moveToPage(page, filtered_playbook_types)">
                                    {{ page }}
                                </button>

                                <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                ...
                                </button>
                                <button v-if="page_index >= 2 && page_index < page_index_array.length -1" class="btn btn-none btn-icon-only-md current-page">
                                    {{ page_index + 1 }}
                                </button>

                                <button v-if="page_index < page_index_array.length -2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                                ...
                                </button>
                                <button :class="page_index === (page_index_array.length -1) ? 'current-page' : ''" class="btn btn-none btn-icon-only-md" @click="moveToPage(page_index_array.length, filtered_playbook_types)" :key="page_index_array.length">
                                    {{ page_index_array.length }}
                                </button>
                            </div>

                            <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click="moveToNextPage(filtered_playbook_types)" :disabled="page_index === page_count -1" data-bs-toggle="tooltip" title="Next page">
                                <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                            </button>
                        </div>
                    </div>
                </div>
            </div>            
        </div>


        <!-- Playbook Description Modal -->
        <playbook-type-information-modal 
            v-if="selectedPlaybook"
            :playbook_type="selectedPlaybook"
            :show="showPlaybookDescriptionModal"
            @update:show="showPlaybookDescriptionModal = $event"
        />

        <!-- Video Tutorial Modal -->
        <div class="modal fade" id="videoTutorialModal" tabindex="-1" aria-labelledby="videoTutorialModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="videoTutorialModalLabel">Introduction to Playbooks</h5>
                        <button type="button" class="btn btn-white float-end close-modal" data-bs-dismiss="modal" aria-label="Close" data-bs-toggle="tooltip" title="Close">
                            <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                        </button>
                    </div>
                    <div class="modal-body p-0">
                        <div class="ratio ratio-16x9">
                            <iframe
                                v-if="videoUrl"
                                :src="videoUrl"
                                title="Introduction to Playbooks Tutorial" 
                                frameborder="0" 
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                allowfullscreen
                            ></iframe>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import permissionsMixin from '../../mixins/permissions';
import tablePaginationMixin from '../../mixins/table_pagination';
import { usePlaybookStore } from '../../stores/playbook';
import { useClientStore } from '../../stores/client';
import PlaybookTypeInformationModal from './PlaybookTypeInformationModal.vue';
import AccordionSelect from '../components/AccordionSelect.vue';

export default {
    props: ['playbooks'],
    mixins: [permissionsMixin, tablePaginationMixin],
    emits: ['open-playbook-creation', 'open-need-help', 'open-dive-deeper'],
    components: { PlaybookTypeInformationModal, AccordionSelect },
    setup() {
        const playbookStore = usePlaybookStore();
        const clientStore = useClientStore();
        return {
            playbookStore,
            clientStore
        };
    },
    computed: {
        client() {
            return this.clientStore.getClient;
        },
        playbook_types() {
            return this.playbookStore.getPlaybookTypes;
        },
        all_asset_types() {
            // Add "All" option first
            const options = [{
                category: "",
                text: "*** All Channels ***",
                value: -1
            }];

            // Extract unique asset types from all playbook types
            const assetTypes = new Set();
            this.local_playbook_types.forEach(playbook_type => {
                if (playbook_type.asset_types) {
                    playbook_type.asset_types.forEach(asset_type => {
                        const assetTypeKey = asset_type.id || asset_type.name;
                        if (!assetTypes.has(assetTypeKey)) {
                            assetTypes.add(assetTypeKey);
                            options.push({
                                category: "",
                                text: asset_type.name,
                                value: asset_type.id || asset_type.name
                            });
                        }
                    });
                }
            });

            // Sort the options (except the first "All" option)
            const sortedOptions = options.slice(1).sort((a, b) => a.text.localeCompare(b.text));
            return [options[0], ...sortedOptions];
        },
        all_strategies() {
            // Add "All" option first
            const options = [{
                category: "",
                text: "*** All Strategic Focuses ***",
                value: -1
            }];

            // Extract unique strategies from all playbook types
            const strategies = new Set();
            this.local_playbook_types.forEach(playbook_type => {
                if (playbook_type.strategy) {
                    playbook_type.strategy.forEach(strategy => {
                        if (!strategies.has(strategy)) {
                            strategies.add(strategy);
                            options.push({
                                category: "",
                                text: strategy,
                                value: strategy
                            });
                        }
                    });
                }
            });

            // Sort the options (except the first "All" option)
            const sortedOptions = options.slice(1).sort((a, b) => a.text.localeCompare(b.text));
            return [options[0], ...sortedOptions];
        }
    },
    data() {
        return {
            local_playbook_types: [],
            filtered_playbook_types: [],
            list_shown: [],
            selectedPlaybook: null,
            showPlaybookDescriptionModal: false,
            viewMode: 'grid', // 'grid' or 'list'
            search_key: '',
            sort_key: 'name',
            sort_order: 'asc',
            filters: {
                asset_type: {
                    category: "",
                    text: "*** All Channels ***",
                    value: -1
                },
                strategy: {
                    category: "",
                    text: "*** All Strategic Focuses ***",
                    value: -1
                }
            },
            // Video modal state
            showVideoModal: false,
            videoUrl: 'https://www.youtube.com/embed/YOUR_VIDEO_ID', // Replace YOUR_VIDEO_ID with your actual YouTube video ID
        };
    },
    beforeMount() {
        this.initializeData();
    },
    methods: {
        initializeData() {
            this.local_playbook_types = [];

            this.playbook_types.forEach(playbook_type => {
                var temp = playbook_type;
                temp.complexity_level = this.getComplexityLevel(temp);
                this.local_playbook_types.push(temp);
            });

            this.local_playbook_types.sort((a, b) => a.name.localeCompare(b.name));
            
            // Initialize pagination
            this.filtered_playbook_types = [...this.local_playbook_types];
            this.reloadPagination(this.filtered_playbook_types);
        },

        getComplexityLevel(play) {
            const assetTypeCount = play.asset_types ? play.asset_types.length : 0;
            
            if (assetTypeCount < 3) {
                return 1;
            } else if (assetTypeCount < 5) {
                return 2;
            } else if (assetTypeCount < 7) {
                return 3;
            } else if (assetTypeCount < 9) {
                return 4;
            } else {
                return 5;
            }
        },

        getEstimatedTimeToCreate(play) {
            if (!play.asset_types || play.asset_types.length === 0) {
                return 'Unknown';
            }

            // Find the maximum days_before_end_date among all asset types
            let maxDays = 0;
            play.asset_types.forEach(assetType => {
                if (assetType.pivot && assetType.pivot.days_before_end_date) {
                    const days = parseInt(assetType.pivot.days_before_end_date);
                    if (days > maxDays) {
                        maxDays = days;
                    }
                }
            });
            
            if (maxDays === 0) {
                return '<img src="/img/icons/avid-icons/infinite.svg" class="icon" alt="">';
            }

            // Round to either days or weeks
            if (maxDays >= 7) {
                const weeks = Math.round(maxDays / 7);
                return `${weeks} week${weeks > 1 ? 's' : ''}`;
            } else {
                return `${maxDays} day${maxDays > 1 ? 's' : ''}`;
            }
        },

        openPlaybookDescription(playbook) {
            this.selectedPlaybook = playbook;
            this.showPlaybookDescriptionModal = true;
        },

        openPlaybookCreation(playbook) {
            // Emit event to parent to open the playbook creation modal
            this.$emit('open-playbook-creation', playbook);
        },

        setViewMode(mode) {
            this.viewMode = mode;
        },

        handleFilterSelection() {
            let filtered = [...this.local_playbook_types];
            
            // Apply search filter
            if (this.search_key && this.search_key.trim() !== '') {
                const searchLower = this.search_key.toLowerCase();
                filtered = filtered.filter(playbook_type => {
                    return playbook_type.name.toLowerCase().includes(searchLower);
                });
            }
            
            // Apply asset type filter
            if (this.filters.asset_type && this.filters.asset_type.value !== -1) {
                filtered = filtered.filter(playbook_type => {
                    return playbook_type.asset_types && playbook_type.asset_types.some(asset_type => 
                        asset_type.id === this.filters.asset_type.value || asset_type.name === this.filters.asset_type.value
                    );
                });
            }
            
            // Apply strategy filter
            if (this.filters.strategy && this.filters.strategy.value !== -1) {
                filtered = filtered.filter(playbook_type => {
                    return playbook_type.strategy && playbook_type.strategy.includes(this.filters.strategy.value);
                });
            }
            
            // Apply sorting
            if (this.sort_key === 'name') {
                if (this.sort_order === 'asc') {
                    filtered.sort((a, b) => a.name.localeCompare(b.name));
                } else {
                    filtered.sort((a, b) => b.name.localeCompare(a.name));
                }
            }
            
            this.filtered_playbook_types = filtered;
            this.reloadPagination(this.filtered_playbook_types);
        },

        getUniqueAssetTypes(asset_types) {
            if (!asset_types) return [];
            const uniqueTypes = new Map();
            asset_types.forEach(type => {
                if (!uniqueTypes.has(type.id)) {
                    uniqueTypes.set(type.id, type);
                }
            });
            return Array.from(uniqueTypes.values());
        },

        sortByKey(key) {
            if (this.sort_key === key) {
                this.sort_order = (this.sort_order === 'asc') ? 'des' : 'asc';
            } else {
                this.sort_key = key;
                this.sort_order = 'asc';
            }
            this.handleFilterSelection();
        },

        openVideoModal() {
            this.showVideoModal = true;
            this.$nextTick(() => {
                const modalElement = document.getElementById('videoTutorialModal');
                if (modalElement) {
                    const existingModal = window.bootstrap.Modal.getInstance(modalElement);
                    if (existingModal) {
                        existingModal.dispose();
                    }
                    const modal = new window.bootstrap.Modal(modalElement);
                    modal.show();
                    
                    // Reset modal state when closed
                    modalElement.addEventListener('hidden.bs.modal', () => {
                        this.showVideoModal = false;
                    }, { once: true });
                }
            });
        }
    }
}
</script>
